import { useContext, useEffect, useState } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { useNavigate, useParams } from "react-router-dom";
import { useValidation } from "../../hooks/useValidation";
import { createNotificationSchema } from "../../validation/notificationSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "../../services/toastService";
import { notificationService } from "../../services/notificationService";
import Loading from "../../components/common/Loading";
import BackButton from "../../components/common/BackButton";

const NotificationForm = () => {
  const { t } = useContext(LocaleContext);
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const initialForm = {
    userName: "",
    mobileNumber: "",
    message: "",
    notificationType: "1",
    title: "",
  };

  useEffect(() => {
    if (isEditMode) loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await editAsync();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    reset(initialForm);
  };

  const v = useValidation();
  const schema = createNotificationSchema(v, t, isEditMode);

  const {
    register,
    handleSubmit,
    reset,
     watch,
    formState: { errors },
  } = useForm({
    defaultValues: initialForm,
    resolver: yupResolver(schema),
  });
  const notificationType = watch("notificationType");

  const editAsync = async () => {
    const response = await notificationService.edit(id);
    if (!response.isSuccess) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      return;
    }
    const data = response.value;
    console.log(data);
    reset({
      userName: data.userName,
      mobileNumber: data.mobileNumber,
      message: data.message,
      notificationType: "1",
      title: data.title,
    });
  };

  const onSubmit = async (values) => {
    try {
      if (isEditMode) {
        await updateAsync(values);
      } else {
        await addAsync(values);
      }
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    }
  };

  const showBackendErrors = (data) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      data.errors.forEach((err) => toast.error(err));
    } else if (data?.message) {
      toast.error(data.message);
    } else {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
    }
  };

  const addAsync = async (values) => {
    try {
      setBtnLoading(true);
      const response = await notificationService.create(values);
      if (!response.isSuccess) {
        showBackendErrors(response);
        return;
      }
      toast.success(t("recordAddedSuccessfully"));
      navigate("/notifications");
    } catch (error) {
      console.log(error);
      showBackendErrors(error?.response?.data);
    } finally {
      setBtnLoading(false);
    }
  };

  const updateAsync = async (values) => {
    try {
      setBtnLoading(true);
      const response = await notificationService.update(id, values);
      if (!response.isSuccess) {
        showBackendErrors(response);
        return;
      }
      toast.success(t("recordUpdatedSuccessfully"));
      navigate("/notifications");
    } catch (error) {
      showBackendErrors(error?.response?.data);
    } finally {
      setBtnLoading(false);
    }
  };

  if (loading) return <Loading />;
  return (
    <section className="section-notification-form">
      <div className="default-heading">{t("notifications")}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-card">
          <div className="my-card-header d-flex justify-content-between align-items-center">
            {isEditMode ? (
              <>
                <i className="fas fa-edit"></i> {t("editOrSelectedNarrator")}
              </>
            ) : (
              <>
                <i className="fas fa-plus plus-icon"></i>{" "}
                {t("addNewNotification")}
              </>
            )}
            <BackButton />
          </div>
          <div className="my-card">
            <div className="my-card-header text-primary flex-space-between">
              {t("basicInformation")}
              <div className="notification-options ">
                <input
                  type="radio"
                  value="1"
                  {...register("notificationType")}
                />
                <label>{t("specific")}</label>
                {!isEditMode && (
                  <>
                    <input
                      type="radio"
                      value="2"
                      {...register("notificationType")}
                    />
                    <label>{t("all")}</label>

                    <input
                      type="radio"
                      value="3"
                      {...register("notificationType")}
                    />
                    <label>{t("clients")}</label>

                    <input
                      type="radio"
                      value="4"
                      {...register("notificationType")}
                    />
                    <label>{t("providers")}</label>
                  </>
                )}
              </div>
            </div>
            <div className="row">
              {notificationType === "1" && (
                <>
                  <div className="col-12">
                    <div className="form-group">
                      <label>{t("userName")}</label>
                      <input
                        type="text"
                        {...register("userName")}
                        className="form-control"
                      />
                      {errors.userName && (
                        <p className="text-danger">{errors.userName.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group">
                      <label>{t("mobileNumber")}</label>
                      <input
                        type="text"
                        {...register("mobileNumber")}
                        className="form-control"
                      />
                      {errors.mobileNumber && (
                        <p className="text-danger">
                          {errors.mobileNumber.message}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("title")}</label>
                  <input
                    type="text"
                    {...register("title")}
                    className="form-control"
                    placeholder={t("title")}
                  />
                  {errors.title && (
                    <p className="text-danger">{errors.title.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("message")}</label>
                  <textarea
                    rows={4}
                    {...register("message")}
                    className="form-control"
                    placeholder={t("message")}
                  />
                  {errors.message && (
                    <p className="text-danger">{errors.message.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 gap-2 d-flex justify-content-end">
            {!btnLoading && (
              <button type="submit" className="btn-default">
                {t("save")}
              </button>
            )}
            {btnLoading && (
              <button type="button" className="btn-default">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
            {!isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
          </div>
        </div>
      </form>
    </section>
  );
};

export default NotificationForm;
